import React from 'react'
import PropTypes from 'prop-types'
import MaskedInput from 'react-text-mask'
import createNumberMask from 'text-mask-addons/dist/createNumberMask'
import { useEffect, useImperativeHandle } from 'react'
import { useState } from 'react'
import { prefix } from '@fortawesome/free-solid-svg-icons'
import { isIDR, roundForCurrency } from '../services/utils/CurrencyUtil'

const buildMaskOptions = (currency, overrides = {}) => {
  const base = {
    prefix: '',
    suffix: '',
    includeThousandsSeparator: true,
    thousandsSeparatorSymbol: '.',
    decimalSymbol: ',',
    integerLimit: 15,
    allowNegative: false,
    allowLeadingZeroes: false,
    keepCharPositions: true,
  }

  if (isIDR(currency)) {
    return { ...base, allowDecimal: false, decimalLimit: 0, ...overrides }
  }

  return { ...base, allowDecimal: true, decimalLimit: 2, ...overrides }
}

const stripMask = (str) =>
  new String(str ?? "").replaceAll(".", "").replace(",", ".")

const numericOfMasked = (masked) => {
  const raw = stripMask(masked)
  if (raw === "") return null
  const numeric = Number(raw)
  return Number.isFinite(numeric) ? numeric : null
}

const numericOfValue = (value) => {
  if (value === "" || value === null || value === undefined) return null
  const numeric = Number(value)
  return Number.isFinite(numeric) ? numeric : null
}

// The mask reads "." as a thousands separator, but a JS number stringifies its fraction with "." —
// handing the value straight to the mask renders 1234.56 as 123.456.
const toMaskedValue = (value, currency) => {
  const numeric = numericOfValue(value)
  if (numeric === null) return ""
  return String(roundForCurrency(numeric, currency)).replace(".", ",")
}

// Bypasses React's value tracker so the programmatic edit still fires onChange.
const nativeValueSetter =
  typeof HTMLInputElement !== "undefined"
    ? Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, "value")?.set
    : undefined

const insertAtCaret = (input, text) => {
  const start = input.selectionStart ?? input.value.length
  const end = input.selectionEnd ?? start
  const next = input.value.slice(0, start) + text + input.value.slice(end)
  if (nativeValueSetter) nativeValueSetter.call(input, next)
  else input.value = next
  input.setSelectionRange(start + text.length, start + text.length)
  input.dispatchEvent(new Event("input", { bubbles: true }))
}

const CurrencyInput = ({
  passedInRef,
  onValueChange,
  onComponentBlur,
  maskOptions,
  currency,
  value,
  onKeyDown,
  ...inputProps
}) => {
  const maskConfig = buildMaskOptions(currency, maskOptions)
  const currencyMask = createNumberMask(maskConfig)

  const [realValue, setRealValue] = useState(undefined)
  const [displayValue, setDisplayValue] = useState(() => toMaskedValue(value, currency))

  // react-text-mask re-conforms from the value prop whenever it differs from the DOM, which wipes a
  // half-typed decimal separator on the keystroke that entered it. Resync only when the parent moved
  // the value somewhere the field is not already showing.
  useEffect(() => {
    if (value === undefined) return
    if (numericOfMasked(displayValue) === numericOfValue(value)) return
    setDisplayValue(toMaskedValue(value, currency))
  }, [value, currency])

  const handleChange = (e) => {
    setDisplayValue(e.target.value)
    const raw = inputProps.prefix ? e.target.value.split(" ")[1] : e.target.value
    const b = stripMask(raw)
    setRealValue(b)
    if (onValueChange) {
      onValueChange(b)
    }
  }

  // The mask's decimal key is "," for every currency, so a non-IDR user's "." — and every numpad
  // decimal key — would otherwise be dropped as a non-digit.
  const handleKeyDown = (e) => {
    const { allowDecimal, decimalSymbol } = maskConfig
    if (
      e.key === "." &&
      allowDecimal &&
      decimalSymbol !== "." &&
      !e.currentTarget.value.includes(decimalSymbol)
    ) {
      e.preventDefault()
      insertAtCaret(e.currentTarget, decimalSymbol)
    }
    if (onKeyDown) {
      onKeyDown(e)
    }
  }

  const handleBlur = (e) => {
    onComponentBlur(e)
  }

  useImperativeHandle(passedInRef, () => ({

    getValue: () => {
      return realValue
    }

  }));

  return <MaskedInput style={{zIndex: 1}} value={displayValue} onChange={handleChange} onKeyDown={handleKeyDown} onBlur={onComponentBlur ? handleBlur : null} mask={currencyMask} {...inputProps} />
}

CurrencyInput.defaultProps = {
  inputMode: 'numeric',
  maskOptions: {},
  currency: undefined,
}

CurrencyInput.propTypes = {
  inputmode: PropTypes.string,
  currency: PropTypes.string,
  maskOptions: PropTypes.shape({
    prefix: PropTypes.string,
    suffix: PropTypes.string,
    includeThousandsSeparator: PropTypes.boolean,
    thousandsSeparatorSymbol: PropTypes.string,
    allowDecimal: PropTypes.boolean,
    decimalSymbol: PropTypes.string,
    decimalLimit: PropTypes.string,
    requireDecimal: PropTypes.boolean,
    allowNegative: PropTypes.boolean,
    allowLeadingZeroes: PropTypes.boolean,
    integerLimit: PropTypes.number,
  }),
}

export default CurrencyInput